// @purpose GLUE
// Scroll + focus after navigation (founder report 2026-10-07: "when I click a link it doesn't scroll to
// the relevant part of that page"). base.html boosts every link (hx-boost on <body>) and turns htmx's
// own scroll-to-top off (scrollIntoViewOnBoost: false, so view transitions don't jump), which left a
// boosted page opening at the previous page's scroll offset and ignoring #fragments. Here:
//   - after a boosted GET swap: #fragment -> scroll that element into view, else -> top of the page;
//     focus moves to the target (or <main>) without a second scroll, for keyboard + screen readers;
//   - a link to the CURRENT page with a fragment written as a path ("/privacy/#data") never refetches
//     the page through htmx: it becomes an in-page jump (pushState + scroll), like a bare "#data";
//   - bare "#fragment" links stay native (htmx never boosts them); CSS gives every [id] a
//     scroll-margin-top for the top bar (static/css/base.css --anchor-offset).
// Smooth scrolling only when the visitor allows motion (prefers-reduced-motion), instant otherwise.

/** The fragment id `href` jumps to when it points at the document at `loc` (same origin, path and
 * query) and carries a non-empty #fragment; otherwise null. Pure, so frontend/tests can cover it. */
export function samePageFragment(href, loc) {
  let url;
  try {
    url = new URL(href, loc.href);
  } catch {
    return null;
  }
  if (!url.hash || url.hash === "#") return null;
  if (url.origin !== loc.origin || url.pathname !== loc.pathname || url.search !== loc.search) return null;
  try {
    return decodeURIComponent(url.hash.slice(1));
  } catch {
    return url.hash.slice(1);
  }
}

/** Should this click be left to the browser (new tab, download, modified, already handled)? */
export function leaveToBrowser(event, link) {
  if (event.defaultPrevented || event.button !== 0) return true;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return true;
  const target = link.getAttribute("target");
  return Boolean((target && target !== "_self") || link.hasAttribute("download"));
}

/** "smooth" unless the visitor asked for reduced motion. */
export function scrollBehavior(win) {
  const reduce = win.matchMedia && win.matchMedia("(prefers-reduced-motion: reduce)").matches;
  return reduce ? "auto" : "smooth";
}

function focusWithoutScroll(el) {
  if (!el) return;
  const focusable = el.matches("a[href], button, input, select, textarea, summary, [tabindex]");
  if (!focusable) el.setAttribute("tabindex", "-1");
  el.focus({ preventScroll: true });
}

/** Scroll `id` into view (offset by its CSS scroll-margin-top) and focus it. False if no such element. */
export function jumpTo(id, doc = document, win = window, behavior = scrollBehavior(win)) {
  const el = id ? doc.getElementById(id) : null;
  if (!el) return false;
  focusWithoutScroll(el);
  el.scrollIntoView({ behavior, block: "start" });
  return true;
}

/** After a boosted page swap: the fragment if any, else the top, focus on <main>. */
export function afterBoostedSwap(anchor, doc = document, win = window) {
  if (anchor && jumpTo(anchor, doc, win)) return "anchor";
  win.scrollTo({ top: 0, left: 0, behavior: "instant" });
  focusWithoutScroll(doc.getElementById("main") || doc.querySelector("main"));
  return "top";
}

export function initAnchors(doc = document, win = window) {
  // capture phase: runs before htmx's boost listener on the link itself, so a same-page fragment link
  // written as a full path is a scroll, not a refetch.
  doc.addEventListener(
    "click",
    (event) => {
      const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (!link || leaveToBrowser(event, link)) return;
      const raw = link.getAttribute("href") || "";
      if (raw.startsWith("#")) return; // native fragment navigation (htmx never boosts these)
      const id = samePageFragment(link.href, win.location);
      if (!id || !doc.getElementById(id)) return;
      event.preventDefault();
      event.stopPropagation();
      if (win.location.hash.slice(1) !== link.hash.slice(1)) win.history.pushState(null, "", link.hash);
      jumpTo(id, doc, win);
    },
    true,
  );

  // htmx fires afterSettle once per settled element; handle each boosted navigation once.
  const handled = new WeakSet();
  doc.addEventListener("htmx:afterSettle", (event) => {
    const d = event.detail || {};
    if (!d.boosted || handled.has(d)) return;
    const verb = (d.requestConfig && d.requestConfig.verb) || "get";
    if (verb.toLowerCase() !== "get") return; // boosted forms keep their own scroll rules
    handled.add(d);
    const anchor = (d.pathInfo && d.pathInfo.anchor) || "";
    let id = anchor;
    try {
      id = decodeURIComponent(anchor);
    } catch {
      /* keep the raw fragment */
    }
    // next frame: after htmx's own settle work and the view transition's DOM update
    win.requestAnimationFrame(() => afterBoostedSwap(id, doc, win));
  });
}
